import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Pill,
  Plus,
  Loader2,
  Calendar,
  ChevronDown,
  ChevronUp,
  Trash2,
  Edit,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  X,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const STATUS_CONFIG = {
  ongoing: { label: 'Ongoing', color: '#4B6651', bg: 'bg-[#4B6651]/10', icon: Clock },
  improved: { label: 'Improved', color: '#4B6651', bg: 'bg-[#4B6651]/10', icon: TrendingUp },
  no_change: { label: 'No Change', color: '#5C5F5C', bg: 'bg-[#F8F7F4]', icon: Minus },
  worsened: { label: 'Worsened', color: '#D94F4F', bg: 'bg-[#D94F4F]/10', icon: TrendingDown },
  resolved: { label: 'Resolved', color: '#2A7D4F', bg: 'bg-[#2A7D4F]/10', icon: CheckCircle },
  discontinued: { label: 'Discontinued', color: '#C06144', bg: 'bg-[#C06144]/10', icon: AlertTriangle }
};

const TYPE_LABELS = {
  medication: 'Medication',
  surgery: 'Surgery',
  rehabilitation: 'Rehabilitation',
  supportive_care: 'Supportive Care',
  dietary: 'Dietary',
  other: 'Other'
};

const EMPTY_FORM = {
  treatment_type: 'medication',
  name: '',
  medications: '',
  dosage: '',
  frequency: '',
  start_date: new Date().toISOString().split('T')[0],
  follow_up_date: '',
  outcome_status: 'ongoing',
  outcome_notes: '',
  notes: ''
};

const TreatmentLog = ({ patientId, examId }) => {
  const [treatments, setTreatments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editingOutcome, setEditingOutcome] = useState(null);
  const [outcomeForm, setOutcomeForm] = useState({ outcome_status: '', outcome_notes: '', follow_up_date: '' });
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [treatRes, sumRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/treatments/patient/${patientId}${params}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treatments/patient/${patientId}/summary`, { withCredentials: true })
      ]);
      setTreatments(treatRes.data.items || []);
      setSummary(sumRes.data);
    } catch (error) {
      logger.error('Error fetching treatments:', error);
    } finally {
      setLoading(false);
    }
  }, [patientId, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Treatment name is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/treatments`, {
        ...form,
        patient_id: patientId,
        exam_id: examId
      }, { withCredentials: true });
      toast.success('Treatment logged');
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log treatment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateOutcome = async (treatmentId) => {
    if (!outcomeForm.outcome_status) return;
    try {
      await axios.put(`${BACKEND_URL}/api/treatments/${treatmentId}/outcome`, outcomeForm, { withCredentials: true });
      toast.success('Outcome updated');
      setEditingOutcome(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update outcome');
    }
  };

  const handleDelete = async (treatmentId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/treatments/${treatmentId}`, { withCredentials: true });
      toast.success('Treatment deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#4B6651]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="treatment-log">
      {/* Summary */}
      {summary && summary.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-[#E5E4E0] bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{summary.total}</p>
              <p className="text-xs text-[#5C5F5C]">Total Treatments</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5E4E0] bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-semibold text-[#4B6651] font-['Work_Sans']">{summary.active}</p>
              <p className="text-xs text-[#5C5F5C]">Active</p>
            </CardContent>
          </Card>
          {Object.entries(summary.status_counts || {}).filter(([k]) => k !== 'ongoing').slice(0, 2).map(([status, count]) => {
            const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.ongoing;
            return (
              <Card key={status} className="border-[#E5E4E0] bg-white">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-semibold font-['Work_Sans']" style={{ color: cfg.color }}>{count}</p>
                  <p className="text-xs text-[#5C5F5C]">{cfg.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Header + Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9 text-sm border-[#E5E4E0]" data-testid="status-filter">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="ongoing">Ongoing</SelectItem>
              <SelectItem value="improved">Improved</SelectItem>
              <SelectItem value="no_change">No Change</SelectItem>
              <SelectItem value="worsened">Worsened</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="discontinued">Discontinued</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#4B6651] hover:bg-[#3E5543] text-white"
          data-testid="new-treatment-btn"
        >
          {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {showForm ? 'Cancel' : 'Log Treatment'}
        </Button>
      </div>

      {/* Create Treatment Form */}
      {showForm && (
        <Card className="border-[#4B6651] bg-white" data-testid="treatment-form">
          <CardHeader>
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <Pill className="w-5 h-5 text-[#4B6651]" />
              New Treatment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Treatment Type *</label>
                  <Select value={form.treatment_type} onValueChange={(v) => setForm({...form, treatment_type: v})}>
                    <SelectTrigger className="border-[#E5E4E0]" data-testid="treatment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABELS).map(([val, label]) => (
                        <SelectItem key={val} value={val}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Treatment Name *</label>
                  <Input
                    placeholder="e.g., Prednisone, Decompressive surgery"
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="border-[#E5E4E0]"
                    data-testid="treatment-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Medications</label>
                  <Input
                    placeholder="Drug names"
                    value={form.medications}
                    onChange={(e) => setForm({...form, medications: e.target.value})}
                    className="border-[#E5E4E0]"
                    data-testid="medications-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Dosage</label>
                  <Input
                    placeholder="e.g., 1mg/kg"
                    value={form.dosage}
                    onChange={(e) => setForm({...form, dosage: e.target.value})}
                    className="border-[#E5E4E0]"
                    data-testid="dosage-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Frequency</label>
                  <Input
                    placeholder="e.g., BID, q12h"
                    value={form.frequency}
                    onChange={(e) => setForm({...form, frequency: e.target.value})}
                    className="border-[#E5E4E0]"
                    data-testid="frequency-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Start Date *</label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({...form, start_date: e.target.value})}
                    className="border-[#E5E4E0]"
                    data-testid="start-date-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Follow-up Date</label>
                  <Input
                    type="date"
                    value={form.follow_up_date}
                    onChange={(e) => setForm({...form, follow_up_date: e.target.value})}
                    className="border-[#E5E4E0]"
                    data-testid="followup-date-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Initial Status</label>
                  <Select value={form.outcome_status} onValueChange={(v) => setForm({...form, outcome_status: v})}>
                    <SelectTrigger className="border-[#E5E4E0]" data-testid="initial-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                        <SelectItem key={val} value={val}>{cfg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Notes</label>
                <Textarea
                  placeholder="Treatment notes, observations..."
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="border-[#E5E4E0] min-h-[80px]"
                  data-testid="treatment-notes-input"
                />
              </div>

              <Button type="submit" disabled={submitting} className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="submit-treatment-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Pill className="w-4 h-4 mr-2" />}
                Log Treatment
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Treatment List */}
      {treatments.length > 0 ? (
        <div className="space-y-3">
          {treatments.map((t) => {
            const cfg = STATUS_CONFIG[t.outcome_status] || STATUS_CONFIG.ongoing;
            const StatusIcon = cfg.icon;
            const isExpanded = expandedId === t.id;

            return (
              <Card key={t.id} className="border-[#E5E4E0] bg-white" data-testid={`treatment-${t.id}`}>
                <CardContent className="p-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cfg.bg}`}>
                        <StatusIcon className="w-5 h-5" style={{ color: cfg.color }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[#2A2C2A]">{t.name}</p>
                          <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ color: cfg.color, backgroundColor: `${cfg.color}15` }}>
                            {cfg.label}
                          </span>
                        </div>
                        <p className="text-xs text-[#5C5F5C]">
                          {TYPE_LABELS[t.treatment_type] || t.treatment_type}
                          {t.medications && ` · ${t.medications}`}
                          {t.dosage && ` · ${t.dosage}`}
                          {t.frequency && ` · ${t.frequency}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingOutcome(editingOutcome === t.id ? null : t.id);
                          setOutcomeForm({ outcome_status: t.outcome_status, outcome_notes: '', follow_up_date: t.follow_up_date || '' });
                        }}
                        className="text-[#4B6651] text-xs"
                        data-testid={`update-outcome-${t.id}`}
                      >
                        <Edit className="w-3 h-3 mr-1" /> Update
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(t.id)}
                        className="text-[#5C5F5C] hover:text-[#D94F4F]"
                        data-testid={`delete-treatment-${t.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setExpandedId(isExpanded ? null : t.id)}>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="flex gap-4 mt-2 text-xs text-[#5C5F5C]">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Started: {t.start_date}
                    </span>
                    {t.follow_up_date && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Follow-up: {t.follow_up_date}
                      </span>
                    )}
                  </div>

                  {/* Outcome Update Inline Form */}
                  {editingOutcome === t.id && (
                    <div className="mt-3 p-3 bg-[#F8F7F4] rounded-lg space-y-2" data-testid={`outcome-form-${t.id}`}>
                      <div className="flex gap-2">
                        <Select value={outcomeForm.outcome_status} onValueChange={(v) => setOutcomeForm({...outcomeForm, outcome_status: v})}>
                          <SelectTrigger className="w-36 h-8 text-xs border-[#E5E4E0] bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_CONFIG).map(([val, c]) => (
                              <SelectItem key={val} value={val}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Follow-up date"
                          type="date"
                          value={outcomeForm.follow_up_date}
                          onChange={(e) => setOutcomeForm({...outcomeForm, follow_up_date: e.target.value})}
                          className="w-36 h-8 text-xs border-[#E5E4E0] bg-white"
                        />
                      </div>
                      <Input
                        placeholder="Outcome notes..."
                        value={outcomeForm.outcome_notes}
                        onChange={(e) => setOutcomeForm({...outcomeForm, outcome_notes: e.target.value})}
                        className="h-8 text-xs border-[#E5E4E0] bg-white"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleUpdateOutcome(t.id)} className="h-7 text-xs bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid={`save-outcome-${t.id}`}>
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingOutcome(null)} className="h-7 text-xs">Cancel</Button>
                      </div>
                    </div>
                  )}

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-[#E5E4E0] space-y-3">
                      {t.notes && (
                        <div>
                          <p className="text-xs font-semibold text-[#5C5F5C] uppercase mb-1">Notes</p>
                          <p className="text-sm text-[#2A2C2A]">{t.notes}</p>
                        </div>
                      )}
                      {t.outcome_notes && (
                        <div>
                          <p className="text-xs font-semibold text-[#5C5F5C] uppercase mb-1">Latest Outcome Notes</p>
                          <p className="text-sm text-[#2A2C2A]">{t.outcome_notes}</p>
                        </div>
                      )}

                      {/* Outcome History */}
                      {t.outcome_history && t.outcome_history.length > 1 && (
                        <div>
                          <p className="text-xs font-semibold text-[#5C5F5C] uppercase mb-2">Outcome History</p>
                          <div className="space-y-1">
                            {t.outcome_history.slice().reverse().map((h, i) => {
                              const hCfg = STATUS_CONFIG[h.status] || STATUS_CONFIG.ongoing;
                              return (
                                <div key={`outcome-${h.date}-${h.status}`} className="flex items-center gap-2 text-xs">
                                  <span className="w-16 text-[#5C5F5C]">{h.date ? new Date(h.date).toLocaleDateString() : ''}</span>
                                  <span className="px-1.5 py-0.5 rounded" style={{ color: hCfg.color, backgroundColor: `${hCfg.color}15` }}>{hCfg.label}</span>
                                  {h.notes && <span className="text-[#5C5F5C]">— {h.notes}</span>}
                                  {h.updated_by && <span className="text-[#5C5F5C] ml-auto">by {h.updated_by}</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <p className="text-xs text-[#5C5F5C]">
                        Created by {t.created_by_name} on {t.created_at ? new Date(t.created_at).toLocaleString() : ''}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-[#E5E4E0] bg-white">
          <CardContent className="p-8 text-center">
            <Pill className="w-10 h-10 text-[#5C5F5C] mx-auto mb-3" />
            <p className="text-[#5C5F5C]">
              {statusFilter !== 'all' ? `No ${STATUS_CONFIG[statusFilter]?.label || statusFilter} treatments` : 'No treatments logged yet'}
            </p>
            <p className="text-sm text-[#5C5F5C] mt-1">Log treatments to track outcomes over time</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TreatmentLog;
